import React, { useEffect, useRef, useState } from 'react';
import { BookmarkIcon, Clock, Share2, Users } from 'lucide-react';
import { cn } from '../../lib/utils';

const collaborationFeatures = [
  {
    icon: Share2,
    title: 'Real-Time Project Sharing',
    description:
      'Invite team members to collaborate on your projects with just a few clicks. Everyone can contribute simultaneously from anywhere.',
  },
  {
    icon: Clock,
    title: 'Seamless Synchronization',
    description:
      'All changes are synced in real-time. Never worry about version conflicts or outdated information again.',
  },
  {
    icon: BookmarkIcon,
    title: 'Resource Sharing',
    description:
      'Share important links with your team using our integrated bookmarks feature for easier team coordination.',
  },
];

export function Collaboration() {
  // State for sections visibility
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  const [areCardsVisible, setAreCardsVisible] = useState(false);
  const [isDiagramVisible, setIsDiagramVisible] = useState(false);
  
  // Refs for each section
  const headerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const diagramRef = useRef<HTMLDivElement>(null);
  
  // Set up intersection observers
  useEffect(() => {
    const headerObserver = new IntersectionObserver(
      ([entry]) => setIsHeaderVisible(entry.isIntersecting),
      { threshold: 0.2 }
    );
    
    const cardsObserver = new IntersectionObserver(
      ([entry]) => setAreCardsVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    
    const diagramObserver = new IntersectionObserver(
      ([entry]) => setIsDiagramVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    
    if (headerRef.current) headerObserver.observe(headerRef.current);
    if (cardsRef.current) cardsObserver.observe(cardsRef.current);
    if (diagramRef.current) diagramObserver.observe(diagramRef.current);
    
    return () => {
      headerObserver.disconnect();
      cardsObserver.disconnect();
      diagramObserver.disconnect();
    };
  }, []);
  
  return (
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-40">
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-purple-800/5 rounded-full blur-3xl opacity-30 pointer-events-none"
      />

      <div className="relative">
        <div ref={headerRef} className={cn(
          "flex flex-col items-center mb-16 text-center transition-all duration-700",
          isHeaderVisible 
            ? "opacity-100 translate-y-0" 
            : "opacity-0 translate-y-20"
        )}>
          <div className="inline-flex items-center justify-center w-20 h-20 mb-8 rounded-2xl bg-gradient-to-br from-purple-500/20 to-blue-500/20 backdrop-blur-sm border border-purple-500/20 transition-all duration-500 hover:shadow-glow hover:border-purple-500/40">
            <Users size={36} className="text-purple-400" />
          </div>

          <h2 className="text-4xl font-bold text-white mb-6 font-['Sora']">Better Together</h2>

          <p className="text-xl text-gray-400 max-w-3xl leading-relaxed">
            Collaboration is at the heart of our platform. Work with your team in real-time, share
            ideas, and move projects forward together seamlessly.
          </p>
        </div>

        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
          {collaborationFeatures.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <div
                key={feature.title}
                className={cn(
                  "group bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-8 transition-all duration-500 hover:bg-gray-800/50 hover:border-purple-500/30",
                  areCardsVisible 
                    ? "opacity-100 translate-y-0" 
                    : "opacity-0 translate-y-20"
                )}
                style={{ transitionDelay: areCardsVisible ? `${index * 150}ms` : '0ms' }}
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-purple-500/10 mb-6 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 group-hover:shadow-glow">
                  <IconComponent size={28} className="text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-4 group-hover:text-purple-300 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            );
          })}
        </div>

        <div 
          ref={diagramRef}
          className={cn(
            "mt-20 p-8 rounded-2xl bg-gradient-to-br from-purple-700/20 to-blue-700/20 backdrop-blur-sm border border-purple-500/20 transition-all duration-700",
            isDiagramVisible 
              ? "opacity-100 translate-y-0" 
              : "opacity-0 translate-y-40"
          )}
        >
          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-8">
            <div className="w-full lg:w-1/2 aspect-video bg-gray-800/50 rounded-xl overflow-hidden p-4 transition-all duration-500 hover:bg-gray-800/70 hover:border-purple-500/20">
              <div className="w-full h-full flex items-center justify-center relative">
                {/* Connection lines - more comprehensive network */}
                <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
                  {/* Person to person connections */}
                  <line
                    x1="30%"
                    y1="50%"
                    x2="50%"
                    y2="50%"
                    stroke="url(#blueGradient)"
                    strokeWidth="1.5"
                  />
                  <line
                    x1="50%"
                    y1="50%"
                    x2="70%"
                    y2="50%"
                    stroke="url(#blueGradient)"
                    strokeWidth="1.5"
                  />

                  {/* Topic connections - vertical */}
                  <line
                    x1="25%"
                    y1="25%"
                    x2="25%"
                    y2="75%"
                    stroke="url(#purpleGradient)"
                    strokeWidth="1.5"
                    strokeDasharray="4 2"
                  />
                  <line
                    x1="75%"
                    y1="25%"
                    x2="75%"
                    y2="75%"
                    stroke="url(#purpleGradient)"
                    strokeWidth="1.5"
                    strokeDasharray="4 2"
                  />

                  {/* Person 1 to all topics */}
                  <line
                    x1="30%"
                    y1="50%"
                    x2="25%"
                    y2="25%"
                    stroke="url(#blueGradient)"
                    strokeWidth="1.5"
                    strokeDasharray="4 2"
                  />
                  <line
                    x1="30%"
                    y1="50%"
                    x2="25%"
                    y2="75%"
                    stroke="url(#greenGradient)"
                    strokeWidth="1.5"
                    strokeDasharray="4 2"
                  />
                  <line
                    x1="30%"
                    y1="50%"
                    x2="75%"
                    y2="25%"
                    stroke="url(#purpleGradient)"
                    strokeWidth="1.5"
                    strokeDasharray="4 2"
                    opacity="0.5"
                  />
                  <line
                    x1="30%"
                    y1="50%"
                    x2="75%"
                    y2="75%"
                    stroke="url(#amberGradient)"
                    strokeWidth="1.5"
                    strokeDasharray="4 2"
                    opacity="0.5"
                  />

                  {/* Person 2 to all topics */}
                  <line
                    x1="50%"
                    y1="50%"
                    x2="25%"
                    y2="25%"
                    stroke="url(#blueGradient)"
                    strokeWidth="1.5"
                    strokeDasharray="4 2"
                    opacity="0.7"
                  />
                  <line
                    x1="50%"
                    y1="50%"
                    x2="25%"
                    y2="75%"
                    stroke="url(#greenGradient)"
                    strokeWidth="1.5"
                    strokeDasharray="4 2"
                    opacity="0.6"
                  />
                  <line
                    x1="50%"
                    y1="50%"
                    x2="75%"
                    y2="25%"
                    stroke="url(#purpleGradient)"
                    strokeWidth="1.5"
                    strokeDasharray="4 2"
                    opacity="0.8"
                  />
                  <line
                    x1="50%"
                    y1="50%"
                    x2="75%"
                    y2="75%"
                    stroke="url(#amberGradient)"
                    strokeWidth="1.5"
                    strokeDasharray="4 2"
                    opacity="0.7"
                  />

                  {/* Person 3 to all topics */}
                  <line
                    x1="70%"
                    y1="50%"
                    x2="25%"
                    y2="25%"
                    stroke="url(#blueGradient)"
                    strokeWidth="1.5"
                    strokeDasharray="4 2"
                    opacity="0.5"
                  />
                  <line
                    x1="70%"
                    y1="50%"
                    x2="25%"
                    y2="75%"
                    stroke="url(#greenGradient)"
                    strokeWidth="1.5"
                    strokeDasharray="4 2"
                    opacity="0.5"
                  />
                  <line
                    x1="70%"
                    y1="50%"
                    x2="75%"
                    y2="25%"
                    stroke="url(#purpleGradient)"
                    strokeWidth="1.5"
                    strokeDasharray="4 2"
                  />
                  <line
                    x1="70%"
                    y1="50%"
                    x2="75%"
                    y2="75%"
                    stroke="url(#amberGradient)"
                    strokeWidth="1.5"
                    strokeDasharray="4 2"
                  />

                  {/* Topic interconnections */}
                  <line
                    x1="25%"
                    y1="25%"
                    x2="75%"
                    y2="25%"
                    stroke="url(#purpleGradient)"
                    strokeWidth="1.5"
                    strokeDasharray="2 2"
                    opacity="0.4"
                  />
                  <line
                    x1="25%"
                    y1="75%"
                    x2="75%"
                    y2="75%"
                    stroke="url(#greenGradient)"
                    strokeWidth="1.5"
                    strokeDasharray="2 2"
                    opacity="0.4"
                  />

                  {/* Gradients */}
                  <defs>
                    <linearGradient id="purpleGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="rgba(147, 51, 234, 0.1)" />
                      <stop offset="100%" stopColor="rgba(147, 51, 234, 0.6)" />
                    </linearGradient>
                    <linearGradient id="blueGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="rgba(59, 130, 246, 0.4)" />
                      <stop offset="100%" stopColor="rgba(59, 130, 246, 0.6)" />
                    </linearGradient>
                    <linearGradient id="greenGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="rgba(16, 185, 129, 0.1)" />
                      <stop offset="100%" stopColor="rgba(16, 185, 129, 0.6)" />
                    </linearGradient>
                    <linearGradient id="amberGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="rgba(217, 119, 6, 0.1)" />
                      <stop offset="100%" stopColor="rgba(217, 119, 6, 0.6)" />
                    </linearGradient>
                  </defs>
                </svg>

                {/* People in the center - with white backgrounds */}
                <div className="absolute left-[30%] top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <img
                    src="https://randomuser.me/api/portraits/men/32.jpg"
                    alt="Team member"
                    className="w-14 h-14 rounded-full object-cover border-2 border-blue-500/50 bg-white"
                  />
                </div>

                <div className="absolute left-[50%] top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <img
                    src="https://randomuser.me/api/portraits/women/44.jpg"
                    alt="Team member"
                    className="w-14 h-14 rounded-full object-cover border-2 border-blue-500/50 bg-white"
                  />
                </div>

                <div className="absolute left-[70%] top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <img
                    src="https://randomuser.me/api/portraits/men/22.jpg"
                    alt="Team member"
                    className="w-14 h-14 rounded-full object-cover border-2 border-blue-500/50 bg-white"
                  />
                </div>

                {/* Notes icon in top left - using FileText from TOPIC_ICONS */}
                <div className="absolute left-[25%] top-[25%] transform -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-lg bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-blue-300 w-7 h-7"
                  >
                    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <line x1="10" y1="9" x2="8" y2="9" />
                  </svg>
                </div>

                {/* Task board in top right - using Kanban from TOPIC_ICONS */}
                <div className="absolute left-[75%] top-[25%] transform -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-lg bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-blue-300 w-7 h-7"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <line x1="8" y1="3" x2="8" y2="21" />
                    <line x1="16" y1="3" x2="16" y2="21" />
                    <line x1="3" y1="9" x2="21" y2="9" />
                    <line x1="3" y1="15" x2="21" y2="15" />
                  </svg>
                </div>

                {/* List in bottom left - using ListTodo from TOPIC_ICONS */}
                <div className="absolute left-[25%] top-[75%] transform -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-lg bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-blue-300 w-7 h-7"
                  >
                    <line x1="8" y1="6" x2="21" y2="6" />
                    <line x1="8" y1="12" x2="21" y2="12" />
                    <line x1="8" y1="18" x2="21" y2="18" />
                    <line x1="3" y1="6" x2="3.01" y2="6" />
                    <line x1="3" y1="12" x2="3.01" y2="12" />
                    <line x1="3" y1="18" x2="3.01" y2="18" />
                  </svg>
                </div>

                {/* AI chat in bottom right - replacing with simpler brain icon */}
                <div className="absolute left-[75%] top-[75%] transform -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-lg bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="28" 
                    height="28" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    className="text-blue-300"
                  >
                    <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 4.44-2.04Z" />
                    <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-4.44-2.04Z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="w-full lg:w-1/2 space-y-6">
              <h3 className="text-2xl font-bold text-white">Work Together, Achieve More</h3>
              <p className="text-gray-400 leading-relaxed">
                Bring your team together in a shared space where ideas flow freely and work gets
                done. Connect, create, and collaborate in real-time.
              </p>
              <ul className="space-y-3">
                {[
                  'Share projects with your team in just a few clicks',
                  "Chat with AI that understands your entire team's context",
                  'Take notes and edit documents together in real-time',
                  'Organize tasks and track progress as a team',
                  'Keep everything in sync across all your workspaces',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-purple-400" />
                    </div>
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
